import { useState, useEffect, FormEvent } from 'react';
import { X, Sparkles, Send, CheckCircle2, ShieldCheck, Loader2 } from 'lucide-react';
import { validatePhone } from '../utils/phone';

interface DiagnosticModalProps {
  isOpen: boolean;
  onClose: () => void;
  preselectedService?: string;
}

export default function DiagnosticModal({ isOpen, onClose, preselectedService }: DiagnosticModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: preselectedService || 'prensa-pr',
    goal: 'lanzamiento',
    details: '',
  });

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (preselectedService) {
      setFormData((prev) => ({ ...prev, service: preselectedService }));
    }
  }, [preselectedService]);

  if (!isOpen) return null;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    // Validar teléfono si se proporcionó
    if (formData.phone.trim()) {
      const phoneValidation = validatePhone(formData.phone);
      if (!phoneValidation.isValid) {
        setErrorMessage(phoneValidation.error || 'Por favor ingresa un número de teléfono válido.');
        return;
      }
    }

    setLoading(true);

    try {
      const phoneValid = formData.phone.trim() ? validatePhone(formData.phone) : null;
      const payload = {
        ...formData,
        phone: phoneValid?.formatted || formData.phone,
      };

      // 1. Intento primario: Endpoint Node.js
      let response = await fetch('/api/request-diagnostic', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      let isJson = response.headers.get('content-type')?.includes('application/json');

      // 2. Si el servidor Node no está disponible (ej. Hosting estático/PHP en Hostinger que devuelve 404 o HTML)
      if (!response.ok || !isJson) {
        response = await fetch('/api/send-email.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        isJson = response.headers.get('content-type')?.includes('application/json');
      }

      if (response.ok && isJson) {
        const data = await response.json();
        if (data.success) {
          setSubmitted(true);
          return;
        } else {
          setErrorMessage(data.error || 'No se pudo procesar el envío del correo.');
        }
      } else {
        setErrorMessage('No se pudo procesar la solicitud en el servidor. Puedes escribirnos directamente a info@epicacomunicaciones.cl');
      }
    } catch (err: any) {
      console.error(err);
      setErrorMessage('Error de conexión con el servidor. Puedes escribirnos directamente a info@epicacomunicaciones.cl');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-slate-950/85 backdrop-blur-md overflow-y-auto">
      <div className="relative w-full max-w-xl max-h-[92vh] overflow-y-auto bg-slate-900 border border-slate-800 rounded-2xl sm:rounded-3xl p-4 sm:p-8 shadow-2xl my-auto">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full bg-slate-800 text-slate-400 hover:text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {submitted ? (
          <div className="text-center py-8 space-y-5">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center mx-auto shadow-lg shadow-emerald-500/10">
              <CheckCircle2 className="w-8 h-8" />
            </div>
            <h3 className="text-2xl font-black text-white">¡Diagnóstico Solicitado!</h3>
            <p className="text-xs text-slate-300 max-w-sm mx-auto leading-relaxed">
              Gracias <span className="font-bold text-white">{formData.name}</span>. ÉPICA Comunicaciones evaluará los antecedentes de {formData.company || 'tu marca'} y coordinará una sesión técnica de 20 minutos.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
              <button
                type="button"
                onClick={() => {
                  setSubmitted(false);
                  setFormData({
                    name: '',
                    email: '',
                    phone: '',
                    company: '',
                    service: preselectedService || 'prensa-pr',
                    goal: 'lanzamiento',
                    details: '',
                  });
                }}
                className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-amber-500 text-slate-950 font-bold text-xs hover:bg-amber-400 transition-colors shadow-md"
              >
                Ingresar nueva solicitud
              </button>
              <button
                type="button"
                onClick={() => {
                  setSubmitted(false);
                  onClose();
                }}
                className="w-full sm:w-auto px-5 py-2.5 rounded-xl bg-slate-800 text-slate-300 font-bold text-xs hover:bg-slate-700 transition-colors"
              >
                Cerrar Ventana
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center text-amber-400">
                <Sparkles className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-xl font-extrabold text-white">Diagnóstico Comunicacional Gratuito</h3>
                <p className="text-xs text-amber-400 font-medium">Épica Comunicaciones Chile</p>
              </div>
            </div>

            <p className="text-xs text-slate-300 leading-relaxed">
              Completa los datos de tu organización para recibir un mapa preliminar de oportunidades periodísticas en los medios líderes de Chile.
            </p>

            {errorMessage && (
              <div className="p-3.5 rounded-xl bg-rose-950/80 border border-rose-500/50 text-rose-200 text-xs leading-relaxed">
                <p className="font-bold text-rose-300 mb-1">⚠️ Error al procesar solicitud:</p>
                <p>{errorMessage}</p>
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-bold text-slate-300 uppercase mb-1">Nombre Completo *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Tu nombre"
                  className="w-full px-3.5 py-2 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:border-amber-500"
                />
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-300 uppercase mb-1">Email Corporativo *</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="nombre@empresa.cl"
                  className="w-full px-3.5 py-2 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:border-amber-500"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-bold text-slate-300 uppercase mb-1">Empresa</label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  placeholder="Nombre de empresa"
                  className="w-full px-3.5 py-2 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:border-amber-500"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-[11px] font-bold text-slate-300 uppercase">
                    Teléfono (Celular / Fijo)
                  </label>
                  {(() => {
                    if (!formData.phone.trim()) return null;
                    const res = validatePhone(formData.phone);
                    if (!res.isValid) {
                      return <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-rose-500/20 text-rose-300 border-rose-500/30">⚠️ Inválido</span>;
                    }
                    if (res.type === 'celular') {
                      return <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-emerald-500/20 text-emerald-300 border-emerald-500/30">📱 Celular Válido</span>;
                    }
                    if (res.type === 'fijo') {
                      return <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-cyan-500/20 text-cyan-300 border-cyan-500/30">☎️ Red Fija Válida</span>;
                    }
                    return <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full border bg-purple-500/20 text-purple-300 border-purple-500/30">🌐 Internacional</span>;
                  })()}
                </div>
                <div className="flex items-center rounded-xl bg-slate-950 border border-slate-800 overflow-hidden focus-within:border-amber-500 transition-colors">
                  <div className="bg-slate-900 border-r border-slate-800 px-3 py-2 flex items-center gap-2 select-none shrink-0">
                    <span className="inline-flex items-center justify-center font-extrabold text-[10px] tracking-wider uppercase px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30 leading-none">
                      CL
                    </span>
                    <span className="text-xs font-bold text-amber-400 leading-none flex items-center">+56</span>
                  </div>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="9 8765 4321 ó 2 2123 4567"
                    className="w-full px-3 py-2 bg-transparent text-white text-xs focus:outline-none placeholder:text-slate-600"
                  />
                </div>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-bold text-slate-300 uppercase mb-1">Servicio de Interés</label>
                <select
                  value={formData.service}
                  onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:border-amber-500"
                >
                  <option value="prensa-pr">Prensa & Relaciones con Medios</option>
                  <option value="comunicacion-estrategica">Comunicación Estratégica</option>
                  <option value="gestion-de-crisis">Gestión de Crisis 24/7</option>
                  <option value="media-training">Media Training</option>
                  <option value="contenido-digital-pr">PR Digital & SEO</option>
                </select>
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-300 uppercase mb-1">Objetivo Inmediato</label>
                <select
                  value={formData.goal}
                  onChange={(e) => setFormData({ ...formData, goal: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:border-amber-500"
                >
                  <option value="lanzamiento">Lanzamiento o Hito de Noticia</option>
                  <option value="posicionamiento-ceo">Posicionamiento de CEO / Voceros</option>
                  <option value="prevencion-crisis">Prevención o Gestión de Crisis</option>
                  <option value="capacitacion">Media Training para Equipo</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-bold text-slate-300 uppercase mb-1">Detalles Adicionales</label>
              <textarea
                rows={3}
                value={formData.details}
                onChange={(e) => setFormData({ ...formData, details: e.target.value })}
                placeholder="Plazos esperados, hitos próximos..."
                className="w-full px-3.5 py-2 rounded-xl bg-slate-950 border border-slate-800 text-white text-xs focus:outline-none focus:border-amber-500"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 px-4 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 font-extrabold text-xs flex items-center justify-center gap-2 shadow-lg shadow-amber-500/20"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
              <span>Solicitar Evaluación de Prensa Sin Costo</span>
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
